import { useParams } from "react-router-dom";
import Button from "components/button";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useEditFreezerModelForm from "./useEditFreezerModelForm";
import { defaultFreezerBrandOptions } from "../add/useAddFreezerModelForm";

export const editFreezerModelPageRouteName = (id?: string) =>
  `/master-data/freezer-model/${id ?? ":id"}/edit`;

const EditFreezerModelPage = () => {
  const { id } = useParams<{ id: string }>();
  const {
    handleSubmit,
    errors,
    register,
    freezerBrand,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    t,
  } = useEditFreezerModelForm(id);

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          {/* Freezer Brand (disabled) */}
          <div className="flex flex-col gap-1.5">
            <Typography variant="bodyLarge">
              {t("label-freezer-brand")}{" "}
              <span className="text-error-500">*</span>
            </Typography>
            <Select
              options={defaultFreezerBrandOptions}
              value={freezerBrand}
              disabled
              data-testid="select-freezer-brand-edit"
            />
          </div>

          {/* Freezer Model Name */}
          <Input
            label={t("label-freezer-model-name")}
            placeholder={t("label-freezer-model-name")}
            required
            {...register("freezerModelName")}
            error={errors.freezerModelName}
            data-testid="input-freezer-model-name-edit"
          />

          {/* Fuel Efficiency */}
          <Input
            label={t("label-fuel-efficiency")}
            placeholder={t("label-fuel-efficiency")}
            type="number"
            enableNumberFormat
            required
            {...register("fuelEfficiency")}
            error={errors.fuelEfficiency}
            rightIcon={
              <Typography variant="body" tone="muted" as="span">
                {t("fuel-efficiency-unit")}
              </Typography>
            }
            data-testid="input-fuel-efficiency-edit"
          />
        </form>
      </section>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={() => setShowCancelModal(true)}
          data-testid="btn-cancel-freezer-edit"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={handleSubmit(onSubmit)}
          data-testid="btn-save-freezer-edit"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleConfirmSave}
        onCancel={() => setShowSaveModal(false)}
        testId="save-freezer-edit-modal"
      />

      {/* Discard Confirmation Modal */}
      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-discard-title")}
        message={t("modal-discard-message")}
        confirmLabel={t("modal-confirm-discard")}
        cancelLabel={t("modal-confirm-stay")}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        testId="discard-freezer-edit-modal"
      />
    </div>
  );
};

export default EditFreezerModelPage;
